import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Line, ComposedChart, Cell,
  Scatter, Legend, LabelList,
} from "recharts";
import { Loader2, Download, RefreshCw, AlertTriangle, TrendingUp, Landmark, Clock } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import SeatMap from "@/components/election/SeatMap";
import { useTableSort } from "@/lib/tableSort";

const hhmm = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const GROUP_LABEL = { biz: "Aldığımız oy", belki: "Belirsiz oy", karsi: "Karşı oy", notr: "Eğilimsiz" };
const GROUP_COLOR = { biz: "#16A34A", belki: "#64748B", karsi: "#DC2626", notr: "#94A3B8" };

const PctLabel = ({ x, y, width, height, value, dark }) => {
  if (!value || height < 13 || width < 14) return null;
  return (
    <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central"
      fontSize={height < 18 ? 9 : 11} fontWeight={700}
      fill={dark ? "#0F172A" : "#FFFFFF"} style={{ pointerEvents: "none" }}>
      {Math.round(value)}
    </text>
  );
};

const TargetTick = ({ cx, cy }) => {
  if (cx == null || cy == null) return null;
  return <line x1={cx - 11} x2={cx + 11} y1={cy} y2={cy} stroke="#000000" strokeWidth={3}
    style={{ pointerEvents: "none" }} />;
};

function Metric({ label, value, sub, color, testid }) {
  return (
    <Card className="p-3.5 border border-border" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-display text-2xl sm:text-3xl font-bold tabular-nums mt-0.5" style={{ color }}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </Card>
  );
}

export default function TrackingCenter({ onOpenCommittee }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partSort, setPartSort] = useState("no");
  const [targetSort, setTargetSort] = useState("gap_worst");
  const tcT = useTableSort([]);

  const load = useCallback(async (manual) => {
    if (manual) setLoading(true);
    try {
      const { data } = await api.get("/election-day/overview");
      setD(data);
    } catch (e) {
      if (manual) toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  if (!d) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Canlı veriler yükleniyor...</div>;

  const t = d.totals;
  const partData = (() => {
    const l = [...d.committees];
    if (partSort === "best") return l.sort((a, b) => b.participation - a.participation);
    if (partSort === "worst") return l.sort((a, b) => a.participation - b.participation);
    if (partSort === "gap_worst") return l.sort((a, b) => (a.part_gap_2018 ?? 0) - (b.part_gap_2018 ?? 0));
    if (partSort === "gap_best") return l.sort((a, b) => (b.part_gap_2018 ?? 0) - (a.part_gap_2018 ?? 0));
    if (partSort === "waiting") return l.sort((a, b) => (b.faal - b.marked) - (a.faal - a.marked));
    return l.sort((a, b) => a.committee_no - b.committee_no);
  })();
  const targetData = (() => {
    const list = d.committees.map((r) => ({
      committee_no: r.committee_no, name: r.name,
      biz_pct: r.biz_pct ?? 0, belki_pct: r.belki_pct ?? 0, karsi_pct: r.karsi_pct ?? 0,
      pred_kesin_pct: r.pred_kesin_pct ?? 0, pred_karsi_pct: r.pred_karsi_pct ?? 0,
      target_gap_pct: r.target_gap_pct ?? 0, votes_to_win: r.votes_to_win ?? 0,
      biz: r.biz, karsi: r.karsi, belki: r.belki, faal: r.faal, lead: r.lead,
    }));
    if (targetSort === "gap_worst") return list.sort((a, b) => a.target_gap_pct - b.target_gap_pct);
    if (targetSort === "gap_best") return list.sort((a, b) => b.target_gap_pct - a.target_gap_pct);
    if (targetSort === "need") return list.sort((a, b) => b.votes_to_win - a.votes_to_win);
    return list.sort((a, b) => a.committee_no - b.committee_no);
  })();
  const dist = ["biz", "belki", "karsi", "notr"].map((k) => ({
    key: k, label: GROUP_LABEL[k], value: t[k],
    pct: t.marked ? Math.round(t[k] / t.marked * 100) : 0,
  }));

  const exportXlsx = () => {
    const token = localStorage.getItem("imeak_token");
    fetch(`${API}/election-day/overview/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "CETELE_CANLI_DURUM.xlsx";
        a.click();
      }).catch(() => toast.error("İndirme başarısız"));
  };

  return (
    <div className="space-y-5" data-testid="tracking-center">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          Canlı tablo · 10 saniyede bir yenilenir · {hhmm(d.server_now)}
        </span>
        {d.scoped_committees && (
          <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300"
            data-testid="tc-scope-note">
            Yalnız yetkili olduğunuz {d.scoped_committees.length} komite gösteriliyor
            {d.scoped_committees.length <= 8 ? ` (K${d.scoped_committees.join(", K")})` : ""}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading} data-testid="tc-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="tc-export">
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="tc-metrics">
        <Metric label="Seçmen" value={t.faal} sub="FAAL firma" color="#475569" testid="tc-faal" />
        <Metric label="Oy Kullanan" value={t.marked} sub={`%${t.participation} katılım`} color="#0F2C59" testid="tc-marked" />
        <Metric label="Aldığımız Oy" value={t.biz} sub={`%${t.marked ? Math.round(t.biz / t.marked * 100) : 0} pay`} color="#16A34A" testid="tc-biz" />
        <Metric label="Hâlâ Beklediğimiz Oy" value={t.biz_pending} sub="aranacak" color="#DC2626" testid="tc-pending" />
        <Metric label="Karşı / Belirsiz" value={`${t.karsi} / ${t.belki}`} sub="karşı oy / belirsiz oy" color="#B45309" testid="tc-karsi" />
        <Metric label="Meclis Üyesi" value={`${t.seats_won}/${t.seats_total}`} sub={`çoğunluk 61 · komite ${t.committees_won}/${t.committees_total}`} color="#7C3AED" testid="tc-seats" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4 border border-border lg:col-span-2" data-testid="tc-flow">
          <h3 className="font-display font-semibold text-base md:text-lg flex items-center gap-2">
            <Clock className="h-4 w-4" /> Saatlik akış
          </h3>
          {d.flow.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4">Henüz işaretleme yok.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={d.flow} margin={{ top: 16, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" name="Saatlik" fill="#0F2C59" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="cumulative" name="Birikimli" stroke="#DC2626" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-4 border border-border" data-testid="tc-dist">
          <h3 className="font-display font-semibold text-base md:text-lg">Gelen oyların dağılımı</h3>
          <div className="mt-3 space-y-2.5">
            {dist.map((x) => (
              <div key={x.key} data-testid={`tc-dist-${x.key}`}>
                <div className="flex justify-between text-sm">
                  <span>{x.label}</span>
                  <span className="font-semibold tabular-nums">{x.value} · %{x.pct}</span>
                </div>
                <div className="h-2 rounded-full bg-muted mt-1 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${x.pct}%`, background: GROUP_COLOR[x.key] }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 border border-border" data-testid="tc-risky">
          <h3 className="font-display font-semibold text-base md:text-lg flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-4 w-4" /> Riskli komiteler (bizde, fark ≤ 10)
          </h3>
          {d.risky.length === 0 ? <p className="text-sm text-muted-foreground mt-3">Riskli komite yok.</p> : (
            <div className="mt-3 space-y-2">
              {d.risky.map((r) => (
                <button key={r.committee_no} type="button" onClick={() => onOpenCommittee?.(r.committee_no)}
                  data-testid={`tc-risky-${r.committee_no}`}
                  className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-sm hover:bg-amber-100 dark:bg-amber-950/20 dark:border-amber-900">
                  <b>K{r.committee_no}</b> {r.name} · önde <b>{r.lead}</b> · bekleyen bizden <b>{r.biz_pending}</b> · {r.seats} meclis
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 border border-border" data-testid="tc-takeable">
          <h3 className="font-display font-semibold text-base md:text-lg flex items-center gap-2 text-emerald-600">
            <TrendingUp className="h-4 w-4" /> 10 oy ve altıyla alınabilecek komiteler
          </h3>
          {d.takeable.length === 0 ? <p className="text-sm text-muted-foreground mt-3">Şu an yok.</p> : (
            <div className="mt-3 space-y-2">
              {d.takeable.map((r) => (
                <button key={r.committee_no} type="button" onClick={() => onOpenCommittee?.(r.committee_no)}
                  data-testid={`tc-takeable-${r.committee_no}`}
                  className="w-full rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-left text-sm hover:bg-emerald-100 dark:bg-emerald-950/20 dark:border-emerald-900">
                  <b>K{r.committee_no}</b> {r.name} · eksik <b>{Math.abs(r.lead) + 1}</b> oy · bekleyen bizden <b>{r.biz_pending}</b> · {r.seats} meclis
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <SeatMap seats={d.seats} />

      <Card className="p-4 border border-border" data-testid="tc-participation-chart">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-display font-semibold text-base md:text-lg">Komite bazında katılım (%)</h3>
          <div className="ml-auto inline-flex flex-wrap rounded-lg border border-border bg-muted p-0.5" data-testid="tc-part-sort">
            {[["no", "Komite No"], ["best", "Katılım ↓"], ["worst", "Katılım ↑"],
              ["gap_worst", "2018'e göre en kötü"], ["gap_best", "2018'e göre en iyi"],
              ["waiting", "Bekleyen en çok"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPartSort(k)} data-testid={`tc-part-sort-${k}`}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${partSort === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={partData} margin={{ top: 12, right: 8, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="committee_no" interval={0} height={34}
              tick={{ fontSize: 13, fontWeight: 700, fill: "currentColor" }} />
            <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
            <Tooltip
              labelFormatter={(v) => { const r = partData.find((x) => x.committee_no === v); return r ? `K${v} ${r.name}` : `K${v}`; }}
              formatter={(val, name, item) => {
                const p = item?.payload || {};
                if (name === "2018 katılımı") {
                  return [`%${val} · fark ${p.part_gap_2018 > 0 ? "+" : ""}${p.part_gap_2018} puan`, name];
                }
                return [`%${val}`, "Bugünkü katılım"];
              }} />
            <Bar dataKey="participation" name="Bugünkü katılım" radius={[3, 3, 0, 0]} onClick={(x) => onOpenCommittee?.(x.committee_no)}>
              {partData.map((r) => (
                <Cell key={r.committee_no} fill={r.winning ? "#16A34A" : r.marked ? "#DC2626" : "#CBD5E1"} cursor="pointer" />
              ))}
            </Bar>
            <Scatter dataKey="part_2018_pct" name="2018 katılımı" legendType="line"
              fill="#000000" shape={TargetTick} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[11px] text-muted-foreground">
          Siyah çizgi: o komitenin <b>2018 seçimindeki katılım oranı</b>. Bar bugünün katılımı;
          çizginin üstüne çıkmak 2018'den iyi gidildiği anlamına gelir.
        </p>
      </Card>

      <Card className="p-4 border border-border" data-testid="tc-target-chart">
        <div className="flex flex-wrap items-center gap-2">
          <div>
            <h3 className="font-display font-semibold text-base md:text-lg">Hedefe yakınlık — tahmin vs çetele (%)</h3>
            <p className="text-[11px] text-muted-foreground">
              Yeşil: çeteleden gelen bizim oy · gri: belirsiz (ortada) · kırmızı: karşı oy ·
              siyah çizgi: 2026 seçim tahminindeki bize oy yüzdesi (sabit hedef) · taban: faal seçmen
            </p>
          </div>
          <div className="ml-auto inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid="tc-target-sort">
            {[["no", "Komite No"], ["gap_best", "Hedefe yakın → uzak"], ["gap_worst", "Hedefe uzak → yakın"], ["need", "Gereken oy ↓"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTargetSort(k)} data-testid={`tc-target-sort-${k}`}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${targetSort === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={targetData} margin={{ top: 14, right: 8, left: -18, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="committee_no" interval={0} height={36}
              tick={{ fontSize: 13, fontWeight: 700, fill: "currentColor" }} />
            <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              labelFormatter={(v) => { const r = targetData.find((x) => x.committee_no === v); return r ? `K${v} ${r.name} · ${r.faal} faal` : `K${v}`; }}
              formatter={(val, name, item) => {
                const p = item?.payload || {};
                if (name === "Hedef (2026 tahmini)") {
                  return [`%${val} · fark ${p.target_gap_pct > 0 ? "+" : ""}${p.target_gap_pct} puan`, name];
                }
                return [`%${val}`, name];
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="biz_pct" name="Bizim oy (çetele)" stackId="tg" fill="#16A34A"
              onClick={(x) => onOpenCommittee?.(x.committee_no)} cursor="pointer">
              <LabelList dataKey="biz_pct" position="center" content={<PctLabel />} />
            </Bar>
            <Bar dataKey="belki_pct" name="Belirsiz" stackId="tg" fill="#94A3B8">
              <LabelList dataKey="belki_pct" position="center" content={<PctLabel dark />} />
            </Bar>
            <Bar dataKey="karsi_pct" name="Karşı oy" stackId="tg" fill="#DC2626">
              <LabelList dataKey="karsi_pct" position="center" content={<PctLabel />} />
            </Bar>
            <Scatter dataKey="pred_kesin_pct" name="Hedef (2026 tahmini)" legendType="line"
              fill="#000000" shape={TargetTick} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs" data-testid="tc-target-table">
            <thead className="bg-muted/60">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-semibold">K</th>
                <th className="px-2 py-1.5 font-semibold">Komite</th>
                <th className="px-2 py-1.5 font-semibold">HEDEF %</th>
                <th className="px-2 py-1.5 font-semibold">BİZİM %</th>
                <th className="px-2 py-1.5 font-semibold">FARK (puan)</th>
                <th className="px-2 py-1.5 font-semibold">KARŞI TAHMİN %</th>
                <th className="px-2 py-1.5 font-semibold">GEREKEN OY</th>
              </tr>
            </thead>
            <tbody>
              {targetData.map((r) => (
                <tr key={r.committee_no} className="border-t border-border cursor-pointer hover:bg-muted/50"
                  onClick={() => onOpenCommittee?.(r.committee_no)} data-testid={`tc-target-row-${r.committee_no}`}>
                  <td className="px-2 py-1 font-semibold">{r.committee_no}</td>
                  <td className="px-2 py-1 max-w-[220px] truncate">{r.name}</td>
                  <td className="px-2 py-1 tabular-nums">%{r.pred_kesin_pct}</td>
                  <td className="px-2 py-1 tabular-nums text-emerald-700 dark:text-emerald-400 font-semibold">%{r.biz_pct}</td>
                  <td className={`px-2 py-1 tabular-nums font-semibold ${r.target_gap_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {r.target_gap_pct > 0 ? `+${r.target_gap_pct}` : r.target_gap_pct}
                  </td>
                  <td className="px-2 py-1 tabular-nums text-muted-foreground">%{r.pred_karsi_pct}</td>
                  <td className="px-2 py-1 tabular-nums">{r.votes_to_win || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-4">
        <Card className="p-0 border border-border overflow-hidden" data-testid="tc-table">
          <div className="p-4 pb-2 flex items-center gap-2">
            <Landmark className="h-4 w-4" />
            <h3 className="font-display font-semibold text-base md:text-lg">48 komite canlı tablosu</h3>
            <span className="text-xs text-muted-foreground ml-auto">Sütun başlığına tıklayarak sıralayın</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur text-xs">
                <tr className="text-left">
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="k" lowBest get={(r) => r.committee_no}>K</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="name" text get={(r) => r.name}>Komite</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="faal" get={(r) => r.faal}>SEÇMEN</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="marked" get={(r) => r.marked}>OY</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="part" get={(r) => r.participation}>KATILIM</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="biz" get={(r) => r.biz}>ALDIĞIMIZ OY</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="pending" get={(r) => r.biz_pending}>HÂLÂ BEKLEDİĞİMİZ OY</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="karsi" get={(r) => r.karsi}>KARŞI OY</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="belirsiz" get={(r) => r.belki}>BELİRSİZ OY</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="lead" get={(r) => r.lead}>FARK</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="seats" get={(r) => r.seats}>MECLİS ÜYESİ</tcT.Th>
                  <tcT.Th className="px-2 py-2 whitespace-nowrap font-semibold" sortKey="last" get={(r) => r.last_mark}>SON</tcT.Th>
                </tr>
              </thead>
              <tbody>
                {tcT.applySort(d.committees).map((r) => (
                  <tr key={r.committee_no} onClick={() => onOpenCommittee?.(r.committee_no)}
                    data-testid={`tc-row-${r.committee_no}`}
                    className="border-t border-border cursor-pointer hover:bg-muted/50">
                    <td className="px-2 py-1.5 font-semibold">{r.committee_no}</td>
                    <td className="px-2 py-1.5 max-w-[240px] truncate">{r.name}</td>
                    <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{r.faal}</td>
                    <td className="px-2 py-1.5 tabular-nums">{r.marked}</td>
                    <td className="px-2 py-1.5 tabular-nums">%{r.participation}</td>
                    <td className="px-2 py-1.5 tabular-nums text-emerald-700 dark:text-emerald-400 font-semibold">{r.biz}</td>
                    <td className="px-2 py-1.5 tabular-nums text-red-600">{r.biz_pending}</td>
                    <td className="px-2 py-1.5 tabular-nums">{r.karsi}</td>
                    <td className="px-2 py-1.5 tabular-nums text-slate-500">{r.belki}</td>
                    <td className={`px-2 py-1.5 tabular-nums font-semibold ${r.lead > 0 ? "text-emerald-600" : r.lead < 0 ? "text-red-600" : ""}`}>
                      {r.lead > 0 ? `+${r.lead}` : r.lead}
                    </td>
                    <td className="px-2 py-1.5 tabular-nums">{r.seats}</td>
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.last_mark ? hhmm(r.last_mark) : "—"}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-border bg-muted/50 font-bold">
                  <td className="px-2 py-2" />
                  <td className="px-2 py-2">TOPLAM</td>
                  <td className="px-2 py-2 tabular-nums">{t.faal}</td>
                  <td className="px-2 py-2 tabular-nums">{t.marked}</td>
                  <td className="px-2 py-2 tabular-nums">%{t.participation}</td>
                  <td className="px-2 py-2 tabular-nums">{t.biz}</td>
                  <td className="px-2 py-2 tabular-nums">{t.biz_pending}</td>
                  <td className="px-2 py-2 tabular-nums">{t.karsi}</td>
                  <td className="px-2 py-2 tabular-nums">{t.belki}</td>
                  <td className={`px-2 py-2 tabular-nums ${t.lead > 0 ? "text-emerald-600" : t.lead < 0 ? "text-red-600" : ""}`}>
                    {t.lead > 0 ? `+${t.lead}` : t.lead}
                  </td>
                  <td className="px-2 py-2 tabular-nums">{t.seats_won}</td>
                  <td className="px-2 py-2" />
                </tr>
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-[11px] text-muted-foreground border-t border-border">
            FARK: belirsiz oylar iki tarafa yarı yarıya dağıtılır, küsurat bizim lehimize yukarı yuvarlanır.
          </p>
        </Card>

        <Card className="p-0 border border-border overflow-hidden" data-testid="tc-recent">
          <div className="p-4 pb-2">
            <h3 className="font-display font-semibold text-base md:text-lg">Son işaretlemeler</h3>
          </div>
          <ScrollArea className="h-[420px]">
            {d.recent.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-muted-foreground">Henüz işaretleme yok.</p>
            ) : d.recent.map((r, i) => (
              <div key={i} className="flex items-start gap-2 border-t border-border px-4 py-2 text-sm" data-testid={`tc-recent-${i}`}>
                <span className="tabular-nums text-xs text-muted-foreground w-10 shrink-0">{hhmm(r.marked_at)}</span>
                <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ background: GROUP_COLOR[r.group] }} />
                <span className="min-w-0 flex-1">
                  <span className="font-semibold">K{r.committee_no}</span> · {r.company_name}
                  <span className="block text-[11px] text-muted-foreground">{r.marked_by}</span>
                </span>
              </div>
            ))}
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
